import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../config/api';
import { useLanguage } from '../context/LanguageContext';
import './Auth.css';

const ResetPassword = () => {
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(null);
  const [success, setSuccess] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenValid(false);
        return;
      }
      try {
        const response = await api.get(`/auth/verify-reset-token/${token}`);
        setTokenValid(response.data.valid);
      } catch {
        setTokenValid(false);
      }
    };
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('resetPassword.mismatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('resetPassword.tooShort'));
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || t('resetPassword.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1>{t('resetPassword.pageTitle')}</h1>
          <p style={{ color: 'var(--text-gray)' }}>{t('resetPassword.checking')}</p>
        </div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1>{t('resetPassword.invalidTitle')}</h1>
          <div className="error-message">
            {t('resetPassword.invalidBody')}
          </div>
          <Link to="/forgot-password" className="btn-submit" style={{ display: 'inline-block', textAlign: 'center', textDecoration: 'none' }}>
            {t('resetPassword.requestNewLink')}
          </Link>
          <p className="auth-link">
            <Link to="/login">{t('resetPassword.backLogin')}</Link>
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1>{t('resetPassword.successTitle')}</h1>
          <div className="success-message" style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '15px',
            borderRadius: '5px',
            marginBottom: '20px',
            border: '1px solid #c3e6cb'
          }}>
            {t('resetPassword.successBody')}
          </div>
          <Link to="/login" className="btn-submit" style={{ display: 'inline-block', textAlign: 'center', textDecoration: 'none' }}>
            {t('resetPassword.goLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>{t('resetPassword.title')}</h1>
        <p style={{ color: 'var(--text-gray)', marginBottom: '1.5rem' }}>
          {t('resetPassword.formIntro')}
        </p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">{t('resetPassword.password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              required
              minLength={6}
              placeholder="••••••••"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t('resetPassword.confirm')}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
              required
              minLength={6}
              placeholder="••••••••"
              disabled={loading}
            />
          </div>

          <button type="submit" className="btn-submit" disabled={loading}>
            {loading ? t('resetPassword.submitting') : t('resetPassword.submit')}
          </button>
        </form>

        <p className="auth-link">
          <Link to="/login">{t('resetPassword.backLogin')}</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;

